// API Domains — shared parsing
// -----------------------------
// One canonical parser for the customer-configured `apiDomains` list, shared by
// the Verify step ([verificationTargets.ts]) and the Add-Application Pentest
// Scope preview ([pentestScope.ts]). Both surfaces MUST parse an entry the same
// way the backend does, so this logic lives in exactly one place — the codebase
// deliberately keeps FE/BE parsing from drifting.
//
// Mirrors the backend's WebApplication._normalizeApiDomainEntry /
// WebAppUnderTestConfiguration._parseApiDomainEntry:
//   * host     — lowercased (host comparison is case-insensitive per DNS)
//   * basePath — collapsed to a single leading slash, trailing slash stripped,
//                query/fragment dropped, path case PRESERVED
// Entries that yield no host are dropped (return null).

export interface ParsedApiDomain {
  host: string;
  basePath: string;
}

// Parse a raw apiDomains entry into { host, basePath }, or null when no host can
// be derived. Accepts a bare host, "host/path", "host:port", or a full URL.
export function parseApiDomainEntry(entry: string): ParsedApiDomain | null {
  const candidate = (entry || "").trim();
  if (!candidate) return null;
  const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(candidate);
  // A bare input starting with "/" is a path with no host — reject before the
  // URL parser (some engines mis-parse "https:///path" as host "path"), matching
  // the backend which drops no-host entries.
  if (!hasScheme && candidate.startsWith("/")) return null;
  try {
    const parsed = new URL(hasScheme ? candidate : `https://${candidate}`);
    const host = parsed.hostname.toLowerCase();
    if (!host) return null;
    // Normalize the path: collapse repeated slashes, ensure a single leading
    // slash, strip trailing slash, path case preserved (e.g. "//rest" ->
    // "/rest", "/rest//v2/" -> "/rest/v2"). Mirrors the backend.
    const pathSegments = parsed.pathname.split("/").filter((segment) => segment.length > 0);
    const basePath = pathSegments.length > 0 ? `/${pathSegments.join("/")}` : "";
    return { host, basePath };
  } catch {
    return null;
  }
}

// Render a parsed entry to the canonical "host" / "host/path" string — the same
// form the backend stores in apiDomains and compares in verifiedDomains.
export function canonicalApiDomain(parsed: ParsedApiDomain): string {
  return parsed.basePath ? `${parsed.host}${parsed.basePath}` : parsed.host;
}

// Coerce the model's apiDomains value into a clean list of entry strings. It
// arrives as a [String] over the wire, EMPTY_STRING by default, or — only as a
// defensive fallback — a bare comma-string.
export function toApiDomainList(apiDomains: unknown): string[] {
  if (Array.isArray(apiDomains)) {
    return apiDomains.filter(
      (domain): domain is string => typeof domain === "string" && domain.trim().length > 0,
    );
  }
  if (typeof apiDomains === "string" && apiDomains.trim().length > 0) {
    return apiDomains
      .split(",")
      .map((domain) => domain.trim())
      .filter((domain) => domain.length > 0);
  }
  return [];
}
